import React, {useEffect, useMemo, useRef, useState} from 'react'
import 'react-date-range/dist/styles.css'
import 'react-date-range/dist/theme/default.css'
import {
  addDays,
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  isSameDay,
} from 'date-fns'
import {
  DateRange,
  DateRangePicker,
  type InputRange,
  type StaticRange,
  type Range,
  type RangeKeyDict,
} from 'react-date-range'
import HomeApi from '../../../Api/home'
import {Stack, Typography} from '@mui/material'
import {useTranslation} from 'react-i18next'
import {useSelector} from 'react-redux'
import {RootState} from '../../../store'
import DateIcon from '../../../svg/Date'

type ApiDatePayload = {start: string; end: string} | {type: string}

function MyComponent({setCards, setLoading}: any) {
  const [showPicker, setShowPicker] = useState(false)
  const [selectedRange, setSelectedRange] = useState<Range>({
    startDate: new Date(),
    endDate: new Date(),
    key: 'selection',
  })

  const store = useSelector((state: RootState) => state.vender)
  const pickerRef = useRef<HTMLDivElement | null>(null)
  const {t} = useTranslation()

  // helper لضمان string من t()
  const ts = (key: string) => (t(key) as unknown as string) ?? ''

  const getAnalysis = async (date: ApiDatePayload) => {
    try {
      if ('start' in date && 'end' in date) {
        let startDate = new Date(date.start)
        const endDate = new Date(date.end)
        if (startDate.getTime() === endDate.getTime()) {
          startDate.setDate(startDate.getDate() - 1)
          const fixed: ApiDatePayload = {
            start: startDate.toISOString().split('T')[0],
            end: date.end,
          }
          HomeApi.getAnalysis(fixed, (res: any) => setCards(res.data), setLoading)
          return
        }
      }
      HomeApi.getAnalysis(date, (res: any) => setCards(res.data), setLoading)
    } catch {
      setLoading(false)
    }
  }

  // النوع الصحيح لـ onChange
  const handleDateSelect = (rangesByKey: RangeKeyDict) => {
    setSelectedRange(rangesByKey.selection as Range)
  }

  const togglePicker = () => setShowPicker((s) => !s)

  useEffect(() => {
    getAnalysis({
      start: format(selectedRange.startDate ?? new Date(), 'y-MM-dd'),
      end: format(selectedRange.endDate ?? new Date(), 'y-MM-dd'),
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [isDay, setIsDay] = useState<string | null>(null)

  const handlePrintRange = async () => {
    if (isDay) {
      await getAnalysis({type: isDay})
      setIsDay(null)
    } else {
      await getAnalysis({
        start: format(selectedRange.startDate ?? new Date(), 'y-MM-dd'),
        end: format(selectedRange.endDate ?? new Date(), 'y-MM-dd'),
      })
    }
    setShowPicker(false)
  }

  const handleOutsideClick = (e: MouseEvent) => {
    if (pickerRef.current && !pickerRef.current.contains(e.target as Node)) {
      setShowPicker(false)
      setSelectedRange({
        startDate: new Date(),
        endDate: new Date(),
        key: 'selection',
      })
    }
  }

  useEffect(() => {
    document.addEventListener('mousedown', handleOutsideClick)
    return () => document.removeEventListener('mousedown', handleOutsideClick)
  }, [])

  // helper لبناء StaticRange مع isSelected
  const makeStatic = (
    label: string,
    rangeFn: () => {startDate: Date; endDate: Date}
  ): StaticRange => ({
    label,
    range: rangeFn,
    isSelected: (currentRange: Range) => {
      const r = rangeFn()
      const s = currentRange.startDate ?? new Date()
      const e = currentRange.endDate ?? new Date()
      return isSameDay(s, r.startDate) && isSameDay(e, r.endDate)
    },
  })

  // ---- Presets (StaticRange[] صحيح بالكامل) ----
  const staticRanges: StaticRange[] = useMemo(
    () => [
      makeStatic(ts('today'), () => ({startDate: new Date(), endDate: new Date()})),
      makeStatic(ts('yesterday'), () => ({
        startDate: addDays(new Date(), -1),
        endDate: addDays(new Date(), -1),
      })),
      makeStatic(ts('thisWeek'), () => ({
        startDate: startOfWeek(new Date()),
        endDate: endOfWeek(new Date()),
      })),
      makeStatic(ts('thisMonth'), () => ({
        startDate: startOfMonth(new Date()),
        endDate: endOfMonth(new Date()),
      })),
      makeStatic(ts('last7Days'), () => ({
        startDate: addDays(new Date(), -6),
        endDate: new Date(),
      })),
      makeStatic(ts('last30Days'), () => ({
        startDate: addDays(new Date(), -29),
        endDate: new Date(),
      })),
    ],
    [t]
  )

  const inputRanges: InputRange[] = useMemo(
    () => [
      {
        label: ts('daysUpToToday'),
        range: (value: number | string) => ({
          startDate: addDays(new Date(), Math.max(Number(value), 1) * -1 + 1),
          endDate: new Date(),
        }),
        getCurrentValue: () => '',
      },
      {
        label: ts('daysStartingToday'),
        range: (value: number | string) => ({
          startDate: new Date(),
          endDate: addDays(new Date(), Number(value) - 1),
        }),
        getCurrentValue: () => '',
      },
    ],
    [t]
  )

  return (
    <div style={{position: 'relative', background: 'var(--bs-app-bg-color)'}}>
      <button
        style={{
          display: 'flex',
          gap: '5px',
          border: 'none',
          alignItems: 'center',
          height: '46px',
          justifyContent: 'center',
          width: '120px',
          borderRadius: '8px',
          background: 'var(--main-color)',
          color: 'var(--w-color)',
          zIndex: 1,
        }}
        onClick={togglePicker}
      >
        <Typography>{format(selectedRange.startDate ?? new Date(), 'y-MM-dd')}</Typography>
        <DateIcon />
      </button>

      <Stack
        style={{
          position: 'absolute',
          right: store.isRtl === 0 ? '0' : 'auto',
          left: store.isRtl === 1 ? '0' : 'auto',
          zIndex: 100,
          background: 'var(--bs-app-footer-bg-color)',
          boxShadow: 'rgba(0, 0, 0, 0.24) 0px 3px 8px',
        }}
        dir='ltr'
      >
        {showPicker && (
          <div ref={pickerRef}>
            <Stack
              sx={{
                display: {md: 'block', xs: 'none'},
                background: 'var(--bs-app-footer-bg-color)',
              }}
            >
              <DateRangePicker
                onChange={handleDateSelect}
                moveRangeOnFirstSelection={false}
                months={2}
                ranges={[selectedRange]}
                direction='horizontal'
                preventSnapRefocus
                calendarFocus='backwards'
                staticRanges={staticRanges}
                inputRanges={inputRanges}
              />
            </Stack>

            <Stack sx={{display: {md: 'none', xs: 'block'}}}>
              <DateRange
                onChange={handleDateSelect}
                moveRangeOnFirstSelection={false}
                months={2}
                ranges={[selectedRange]}
                calendarFocus='backwards'
              />
            </Stack>

            <Stack direction='row' justifyContent='flex-end' alignItems='flex-start'>
              <button
                style={{
                  display: 'flex',
                  gap: '5px',
                  border: 'none',
                  alignItems: 'center',
                  height: '46px',
                  justifyContent: 'center',
                  width: '120px',
                  borderRadius: '8px',
                  margin: '20px 0',
                }}
                onClick={() => {
                  setSelectedRange({
                    startDate: new Date(),
                    endDate: new Date(),
                    key: 'selection',
                  })
                  setShowPicker(false)
                }}
              >
                {ts('cancel')}
              </button>
              <button
                style={{
                  display: 'flex',
                  gap: '5px',
                  border: 'none',
                  alignItems: 'center',
                  height: '46px',
                  justifyContent: 'center',
                  width: '120px',
                  borderRadius: '8px',
                  margin: '20px',
                  background: '#3d91ff',
                  color: 'white',
                }}
                onClick={handlePrintRange}
              >
                {ts('apply')}
              </button>
            </Stack>
          </div>
        )}
      </Stack>
    </div>
  )
}

export default MyComponent
